<template>
  <UITagsList
    v-model="selected"
    :opened="show"
    :readonly="disabled"
    @plus:click="show = !show"
    @item:rename="handleRenameTag"
  />
  <TagsListDropdown
    v-if="show"
    v-model="selectedTag"
    :disabled="loading"
    :size
    :selected
    @select="handleSelectTag"
  />
</template>

<script setup lang="ts">
import { computed, inject, onBeforeMount, ref } from 'vue'

import { CommonTag, InputSize } from '@types'
import { TaggingRuleAction } from '../utils/types'

import { NOTIFICATION_DELAY } from '@/const/common'
import { handleCatchedError } from '@/helpers/common'
import { useNotifications } from '@/plugins/notification'
import { useLinkedDataTransactionsTagsStore } from '@/store/linkedData/transactionsTags'

import { UITagsList } from '@ui/core'
import TagsListDropdown from '@/views/LinkedData/Transactions/components/TagsListDropdown.vue'

type Props = {
  disabled?: boolean
}

defineProps<Props>()

const modelValue = defineModel<TaggingRuleAction>({
  required: true,
})

defineOptions({
  inheritAttrs: false,
})

const size = inject<InputSize>('size')

const selectedTag = ref<string>()

const { progress, remove, update } = useNotifications()
const linkedDataTransactionsTagsStore = useLinkedDataTransactionsTagsStore()

const tagsList = computed(() => linkedDataTransactionsTagsStore.getList)

const show = ref(false)
const loading = computed(() => linkedDataTransactionsTagsStore.loading)

const selected = computed<CommonTag[]>({
  get() {
    return tagsList.value.filter(tag =>
      modelValue.value.tag_ids?.includes(`${tag.id}`),
    )
  },
  set(values) {
    modelValue.value = {
      ...modelValue.value,
      tag_ids: values.map(tag => `${tag.id}`),
    }
  },
})

const handleSelectTag = (value: CommonTag) => {
  selected.value = [...selected.value, value]
  selectedTag.value = undefined
}

const handleRenameTag = async (id: string, name: string) => {
  const nid = await progress({
    message: 'Updating tag',
  })
  try {
    const result = await linkedDataTransactionsTagsStore.update({
      id,
      name,
    })
    if (nid) {
      await update(
        nid,
        {
          type: 'success',
          message: `Tag ${result?.name} updated`,
        },
        NOTIFICATION_DELAY,
      )
    }
  } catch (e) {
    if (nid) await remove(nid)
    handleCatchedError(e as string, { id, name })
  }
}

onBeforeMount(() => {
  if (selected.value.length) return
  selected.value = []
})
</script>
